import {
  CascadedShadowGenerator,
  Color3,
  Color4,
  DirectionalLight,
  HemisphericLight,
  Scene,
  Vector3,
  type Engine,
  type WebGPUEngine,
} from '@babylonjs/core';
import { PALETTE } from './palettes.js';

export interface SceneSetup {
  scene: Scene;
  sun: DirectionalLight;
  ambient: HemisphericLight;
  shadowGenerator: CascadedShadowGenerator;
  /** Horizon RGB used by sky/fog/clear color — apply when building Sky to keep them aligned. */
  horizonColor: Color3;
}

export function createScene(engine: Engine | WebGPUEngine): SceneSetup {
  const scene = new Scene(engine);

  // Sky/fog/clear share the active palette's horizon color so unloaded chunk seams
  // blend invisibly into the distance.
  const horizonColor = PALETTE.horizon;

  scene.clearColor = new Color4(horizonColor.r, horizonColor.g, horizonColor.b, 1.0);
  scene.ambientColor = PALETTE.ambient;
  scene.fogMode = Scene.FOGMODE_LINEAR;
  scene.fogColor = horizonColor;
  scene.fogStart = PALETTE.fogStart;
  scene.fogEnd = PALETTE.fogEnd;

  const ambient = new HemisphericLight('ambient', new Vector3(0.3, 1, 0.2), scene);
  ambient.intensity = 0.6;
  // Sky tint above, ground tint below — Ghibli trick: bouncing green light up into
  // shadows is what sells the vibrant grass (in either palette).
  ambient.diffuse = PALETTE.hemiSky;
  ambient.groundColor = PALETTE.hemiGround;

  const sun = new DirectionalLight('sun', new Vector3(-0.5, -1, -0.3).normalize(), scene);
  sun.intensity = PALETTE.sunIntensity;
  sun.diffuse = PALETTE.sun;
  sun.position = new Vector3(200, 400, 200);

  // Bigger maps + farther reach so distant trees/buildings still cast.
  const shadowGenerator = new CascadedShadowGenerator(2048, sun);
  shadowGenerator.numCascades = 3;
  shadowGenerator.lambda = 0.85;
  shadowGenerator.usePercentageCloserFiltering = true;
  shadowGenerator.filteringQuality = CascadedShadowGenerator.QUALITY_MEDIUM;
  shadowGenerator.bias = 0.005;
  shadowGenerator.normalBias = 0.02;
  shadowGenerator.shadowMaxZ = 400;
  shadowGenerator.stabilizeCascades = true;

  return { scene, sun, ambient, shadowGenerator, horizonColor };
}
